import {
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
  inject,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import {
  InMemoryScrollingFeature,
  InMemoryScrollingOptions,
  provideRouter,
  RouteReuseStrategy,
  withEnabledBlockingInitialNavigation,
  withInMemoryScrolling,
} from '@angular/router';
import { appRoutes } from './app.routes';
import {
  provideClientHydration,
  withEventReplay,
  withNoIncrementalHydration
} from '@angular/platform-browser';
import {
  HTTP_INTERCEPTORS,
  HttpClientModule,
  provideHttpClient,
  withFetch,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { Common } from '@meplis/services/common';
import { CommonRouterBasedService } from '@meplis/services/common-router-based';
import { FileWebService } from '@meplis/services/file-web';
import { Hub } from '@meplis/services/hub';
import { HubWebService } from '@meplis/services/hub-web';
import { MepFile } from '@meplis/services/mep-file';
import { MepStorage } from '@meplis/services/mep-storage';
import { E2eeKeyStore, E2eeKeyStoreWeb } from '@cnp/e2ee/store';
import { PlatformService } from '@meplis/services/platform';
import { ProfileService } from '@meplis/services/profile';
import { StorageWebService } from '@meplis/services/storage-web';
import {
  provideTranslateService,
  TranslateService,
} from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { provideAnimations } from '@angular/platform-browser/animations';
import { shareIcons } from 'ngx-sharebuttons/icons';
import { DynamicHtmlModule } from '@meplis/components/dynamic-html';
import { FilepickerInsertComponent } from '@meplis/components/filepicker-insert';
import { FilesVideoComponent } from '@meplis/components/files-video';
import { SocialExternalLinkComponent } from '@meplis/components/social-external-link';
import { SocialHashtagLinkComponent } from '@meplis/components/social-hashtag-link';
import { MentionLinkComponent } from './shared/components/common/profile/mention-link/mention-link.component';
import { TokenInterceptor } from '@meplis/services/token-interceptor';
import { appInitializer } from './app.initializer';
import { AuthenticationService as _authenticationCnpService } from './shared/services/application/authentication.service';
import { SEOService as _cnpSEOService } from './shared/services/application/seo.service';
import { AuthenticationService } from '@meplis/services/authentication';
import { SEOService } from '@meplis/services/seo';
import { GoogleTagManagerModule } from 'angular-google-tag-manager';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { CommonService } from './shared/services/application/common.service';
import { CustomReuseRouteService } from './shared/services/application/custom-reuse-route.service';
import { SocialService } from './shared/services/application/social.service';
import { AppErrorHandler } from './shared/services/application/app-error-handler';
import { environment } from '@meplis/environments';
import { APP_COMMIT } from './version';

/**
 * The translation files keep the same path across deploys, so the browser
 * decides on its own whether to ask for them again. Builds before
 * 1.0.0-beta.170 served every unhashed file with `max-age=1y`, which left
 * those browsers holding a year-old copy that a reload does not revalidate --
 * a key added after that release read as `EVENTS.SPEAKER_READ_BIO` until the
 * cache was cleared by hand.
 *
 * The commit makes it a new URL on every deploy, so a stale entry is never
 * the one asked for, whatever the old response promised.
 */
const scrollConfig: InMemoryScrollingOptions = {
  scrollPositionRestoration: 'disabled',
  anchorScrolling: 'enabled',
};

const inMemoryScrollingFeature: InMemoryScrollingFeature =
  withInMemoryScrolling(scrollConfig);

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(
      appRoutes,
      inMemoryScrollingFeature,
      withEnabledBlockingInitialNavigation()
    ),
    provideAppInitializer(() => {
      const initializerFn = appInitializer(
        inject(_authenticationCnpService),
        inject(CommonService),
        inject(SocialService),
        inject(TranslateService)
      );
      return initializerFn();
    }),
    provideClientHydration(withEventReplay(), withNoIncrementalHydration()),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideHttpClient(withFetch(), withInterceptorsFromDi()),
    provideAnimations(),
    // Registers the icons (facebook-f, whatsapp, telegram-plane, link, etc.)
    // that <share-buttons> (ngx-sharebuttons) needs into the same global
    // FaIconLibrary singleton our own icons use -- it doesn't ship its own
    // icon set, it renders through @fortawesome/angular-fontawesome too.
    shareIcons(),
    {
      provide: RouteReuseStrategy,
      useClass: CustomReuseRouteService,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TokenInterceptor,
      multi: true,
    },
    // The ?v= suffix busts the browser/CDN cache on every deploy -- without
    // it, a translation key added after a release read as its own raw key
    // until the cached i18n/en.json finally expired.
    provideTranslateService({
      loader: provideTranslateHttpLoader({
        prefix: '/i18n/',
        suffix: `.json?v=${APP_COMMIT}`,
      }),
    }),
    importProvidersFrom(
      GoogleTagManagerModule.forRoot({ id: environment.google.gtmId }),
      MatSnackBarModule,
      DynamicHtmlModule.forRoot({
        components: [
          {
            component: FilepickerInsertComponent,
            selector: 'filepicker-insert',
          },
          {
            // This app's own: opens the profile popover instead of navigating
            // away. See MentionLinkComponent.
            component: MentionLinkComponent,
            selector: 'mec-social-profile-link',
          },
          {
            component: SocialHashtagLinkComponent,
            selector: 'mec-social-hashtag-link',
          },
          {
            component: SocialExternalLinkComponent,
            selector: 'mec-social-external-link',
          },
          {
            component: FilesVideoComponent,
            selector: 'files-video',
          },
        ],
      })
    ),
    {
      provide: ErrorHandler,
      useClass: AppErrorHandler,
    },
    {
      provide: AuthenticationService,
      useExisting: _authenticationCnpService,
    },
    {
      provide: SEOService,
      useClass: _cnpSEOService,
    },
    {
      provide: Common,
      useClass: CommonRouterBasedService,
    },
    {
      provide: Hub,
      useClass: HubWebService,
    },
    {
      provide: MepStorage,
      useClass: StorageWebService,
    },
    {
      provide: MepFile,
      useClass: FileWebService,
    },
    {
      provide: E2eeKeyStore,
      useClass: E2eeKeyStoreWeb,
    },
  ],
};
